/* ── XSS sanitizer: always escape before injecting into innerHTML ── */
const esc = str => String(str ?? '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#x27;')
  .replace(/\//g, '&#x2F;');

document.addEventListener('DOMContentLoaded', () => {

  /* ══════════════════════════════════════════════
     0. HEADER — floating glass, scrolled state
  ══════════════════════════════════════════════ */
  const headerEl = document.querySelector('.header');
  if (headerEl) {
    const onScroll = () => {
      headerEl.classList.toggle('header--scrolled', window.scrollY > 60);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  /* ══════════════════════════════════════════════
     1. SCROLL REVEALS — GSAP ScrollTrigger (CSS fallback)
  ══════════════════════════════════════════════ */
  const isMobile            = window.innerWidth < 640;
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const cardEls   = document.querySelectorAll('.product-card, .why-card, .review-card, .cat-card, .col-card');
  const headerEls = document.querySelectorAll('.section__header');

  if (typeof gsap !== 'undefined' && typeof ScrollTrigger !== 'undefined') {
    gsap.registerPlugin(ScrollTrigger);

    if (prefersReducedMotion) {
      gsap.set([...cardEls, ...headerEls], { opacity: 0 });
      ScrollTrigger.batch([...cardEls, ...headerEls], {
        start: 'top 95%',
        onEnter: batch => gsap.to(batch, { opacity: 1, duration: 0.35, stagger: 0.03 }),
        once: true
      });
    } else {
      gsap.set(cardEls,   { opacity: 0, y: 14, filter: 'blur(6px)' });
      gsap.set(headerEls, { opacity: 0, y: 10, filter: 'blur(4px)' });

      /* one-shot reveal — cards animate in once and stay visible */
      ScrollTrigger.batch(cardEls, {
        start: 'top 92%',
        onEnter: batch => gsap.to(batch, {
          opacity: 1, y: 0, filter: 'blur(0px)',
          stagger: isMobile ? 0.05 : 0.09,
          duration: 0.75, ease: 'power3.out',
          clearProps: 'filter', overwrite: true
        }),
        once: true
      });

      headerEls.forEach(el => gsap.to(el, {
        opacity: 1,
        y: 0,
        filter: 'blur(0px)',
        duration: 0.7,
        ease: 'power3.out',
        clearProps: 'filter',
        scrollTrigger: { trigger: el, start: 'top 88%', end: 'bottom 12%', toggleActions: 'play none none reverse' }
      }));
    }
  } else {
    /* CSS fallback when GSAP is unavailable */
    const revealObs = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        const el = entry.target;
        setTimeout(() => el.classList.add('is-revealed'), parseInt(el.dataset.revealDelay) || 0);
        revealObs.unobserve(el);
      });
    }, { threshold: 0.07, rootMargin: '0px 0px -32px 0px' });

    cardEls.forEach(el => {
      const siblings = Array.from(el.parentElement?.children || []);
      el.dataset.revealDelay = siblings.indexOf(el) * (isMobile ? 40 : 80);
      el.classList.add('reveal-stagger');
      revealObs.observe(el);
    });
    headerEls.forEach(el => {
      el.classList.add('reveal-up');
      revealObs.observe(el);
    });
  }

  /* ══════════════════════════════════════════════
     2. MOBILE MENU
  ══════════════════════════════════════════════ */
  const menuBtn     = document.getElementById('mobile-menu-btn');
  const mobileNav   = document.getElementById('mobile-nav');
  const mobileOver  = document.getElementById('mobile-overlay');
  const mobileClose = document.getElementById('mobile-nav-close');

  const openMenu  = () => {
    mobileNav?.classList.add('is-open');
    mobileOver?.classList.add('is-visible');
    document.body.classList.add('no-scroll');
  };
  const closeMenu = () => {
    mobileNav?.classList.remove('is-open');
    mobileOver?.classList.remove('is-visible');
    document.body.classList.remove('no-scroll');
  };

  menuBtn?.addEventListener('click', openMenu);
  mobileClose?.addEventListener('click', closeMenu);
  mobileOver?.addEventListener('click', closeMenu);

  /* ══════════════════════════════════════════════
     3. CART DRAWER
  ══════════════════════════════════════════════ */
  const cartDrawer = document.getElementById('cart-drawer');
  const cartOver   = document.getElementById('cart-overlay');
  const cartClose  = document.getElementById('cart-close');
  const cartBody   = document.getElementById('cart-body');
  const cartSub    = document.getElementById('cart-subtotal');

  /* GSAP initial state — overrides CSS so transitions don't fight */
  if (typeof gsap !== 'undefined' && cartDrawer) {
    gsap.set(cartDrawer, { x: '100%', visibility: 'visible' });
    gsap.set(cartOver,   { autoAlpha: 0 });
  }

  const openCart = () => {
    document.body.classList.add('no-scroll');
    fetchCart();
    if (typeof gsap !== 'undefined' && cartDrawer) {
      gsap.to(cartDrawer, { x: '0%',  duration: 0.58, ease: 'expo.out' });
      gsap.to(cartOver,   { autoAlpha: 1, duration: 0.32 });
    } else {
      cartDrawer?.classList.add('is-open');
      cartOver?.classList.add('is-visible');
    }
  };

  const closeCart = () => {
    document.body.classList.remove('no-scroll');
    if (typeof gsap !== 'undefined' && cartDrawer) {
      gsap.to(cartDrawer, { x: '100%', duration: 0.42, ease: 'power3.in' });
      gsap.to(cartOver,   { autoAlpha: 0, duration: 0.28 });
    } else {
      cartDrawer?.classList.remove('is-open');
      cartOver?.classList.remove('is-visible');
    }
  };

  cartClose?.addEventListener('click', closeCart);
  cartOver?.addEventListener('click', closeCart);

  document.querySelectorAll('[data-open-cart]').forEach(el =>
    el.addEventListener('click', e => {
      e.preventDefault();
      if (typeof gsap !== 'undefined') {
        gsap.timeline()
          .to(el, { x: 32, opacity: 0, duration: 0.22, ease: 'power2.in' })
          .call(openCart)
          .set(el,  { x: -8 })
          .to(el,   { x: 0, opacity: 1, duration: 0.38, ease: 'back.out(1.6)' });
      } else {
        openCart();
      }
    })
  );

  const money = cents =>
    (cents / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' });

  const updateCount = (n) => {
    document.querySelectorAll('[data-cart-count-num]').forEach(el => {
      el.textContent = n > 0 ? n : '';
    });
  };

  const fetchCart = async () => {
    try {
      const res  = await fetch('/cart.js');
      const cart = await res.json();
      renderCart(cart);
    } catch (e) { /* offline / dev */ }
  };

  const changeQty = async (key, qty) => {
    await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity: qty })
    });
    fetchCart();
  };

  const renderCart = (cart) => {
    updateCount(cart.item_count);
    if (cartSub) cartSub.textContent = money(cart.total_price);
    if (!cartBody) return;

    if (cart.items.length === 0) {
      cartBody.innerHTML = `
        <div class="cart-empty">
          <p>Your cart is empty.</p>
          <a href="/collections/all" class="cart-empty__cta">Start shopping →</a>
        </div>`;
      return;
    }

    cartBody.innerHTML = cart.items.map(item => `
      <div class="cart-item">
        <div class="cart-item__img">
          <img src="${esc(item.image)}" alt="${esc(item.product_title)}">
        </div>
        <div class="cart-item__info">
          <div class="cart-item__title">${esc(item.product_title)}</div>
          ${item.variant_title !== 'Default Title'
            ? `<div class="cart-item__variant">${esc(item.variant_title)}</div>` : ''}
          <div class="cart-item__row">
            <div class="cart-item__qty">
              <button class="cqty" data-key="${esc(item.key)}" data-q="${item.quantity - 1}">−</button>
              <span>${item.quantity}</span>
              <button class="cqty" data-key="${esc(item.key)}" data-q="${item.quantity + 1}">+</button>
            </div>
            <span class="cart-item__price">${money(item.line_price)}</span>
          </div>
        </div>
        <button class="cart-item__remove" data-key="${esc(item.key)}" aria-label="Remove">×</button>
      </div>
    `).join('');

    if (typeof gsap !== 'undefined') {
      gsap.from(cartBody.querySelectorAll('.cart-item'), {
        y: 30, opacity: 0, scale: 0.88,
        duration: 0.42, stagger: 0.08,
        ease: 'back.out(1.6)',
        clearProps: 'all'
      });
    }

    cartBody.querySelectorAll('.cqty').forEach(btn =>
      btn.addEventListener('click', () => {
        spawnCartCat(btn);
        changeQty(btn.dataset.key, parseInt(btn.dataset.q));
      })
    );
    cartBody.querySelectorAll('.cart-item__remove').forEach(btn =>
      btn.addEventListener('click', () => changeQty(btn.dataset.key, 0))
    );
  };

  /* ══════════════════════════════════════════════
     4. ADD TO CART — AJAX + animation (GSAP timeline)
  ══════════════════════════════════════════════ */
  const animateAtc = (btn) => {
    if (!btn || btn.dataset.busy) return;
    const textEl = btn.querySelector('.pdp__atc-text');
    if (!textEl) return;
    const orig = textEl.textContent;
    btn.dataset.busy = '1';

    if (typeof gsap !== 'undefined') {
      gsap.timeline({ onComplete: () => delete btn.dataset.busy })
        .to(btn,    { scale: 0.975, duration: 0.13, ease: 'power2.in' })
        .to(btn,    { scale: 1,     duration: 0.15, ease: 'power2.out' })
        .add(() => btn.classList.add('is-loading'))
        .to(textEl, { opacity: 0,   duration: 0.15 }, '+=0.82')
        .add(() => {
          btn.classList.remove('is-loading');
          btn.classList.add('is-done');
          textEl.textContent = '✓  Added to Cart';
        })
        .to(textEl, { opacity: 1,   duration: 0.15 })
        .to(textEl, { opacity: 0,   duration: 0.2  }, '+=2.2')
        .add(() => { btn.classList.remove('is-done'); textEl.textContent = orig; })
        .to(textEl, { opacity: 1,   duration: 0.2  });
    } else {
      /* setTimeout fallback */
      btn.style.transform = 'scale(0.975)';
      setTimeout(() => (btn.style.transform = 'scale(1)'), 130);
      btn.classList.add('is-loading');
      setTimeout(() => {
        btn.classList.remove('is-loading');
        textEl.style.opacity = '0';
        setTimeout(() => {
          btn.classList.add('is-done');
          textEl.textContent = '✓  Added to Cart';
          textEl.style.opacity = '1';
          setTimeout(() => {
            textEl.style.opacity = '0';
            setTimeout(() => {
              btn.classList.remove('is-done');
              textEl.textContent = orig;
              textEl.style.opacity = '1';
              delete btn.dataset.busy;
            }, 200);
          }, 2200);
        }, 160);
      }, 820);
    }
  };

  document.querySelectorAll('form[action*="cart/add"]').forEach(form => {
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const btn = form.querySelector('.pdp__atc');
      animateAtc(btn);
      try {
        await fetch('/cart/add.js', { method: 'POST', body: new FormData(form) });
        await fetchCart();
        setTimeout(openCart, 920);
      } catch (err) { /* dev */ }
    });
  });

  /* ══════════════════════════════════════════════
     5. QUICK ADD (collection cards)
  ══════════════════════════════════════════════ */
  document.querySelectorAll('[data-quick-add]').forEach(btn => {
    btn.addEventListener('click', async (e) => {
      e.preventDefault();
      e.stopPropagation();
      const id = btn.dataset.variantId;
      if (!id || btn.dataset.busy) return;
      btn.dataset.busy = '1';
      const orig = btn.textContent;
      btn.textContent = 'Adding…';
      try {
        await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id, quantity: 1 })
        });
        await fetchCart();
        btn.textContent = '✓ Added';
        setTimeout(() => { btn.textContent = orig; delete btn.dataset.busy; }, 2000);
        openCart();
      } catch (err) { btn.textContent = orig; delete btn.dataset.busy; }
    });
  });

  /* ══════════════════════════════════════════════
     6. COLLECTION SORT
  ══════════════════════════════════════════════ */
  document.getElementById('sort-by')?.addEventListener('change', function () {
    const url = new URL(window.location.href);
    url.searchParams.set('sort_by', this.value);
    window.location.href = url.toString();
  });

  /* ══════════════════════════════════════════════
     7. CART QTY — tiny cat pop animation
  ══════════════════════════════════════════════ */
  const spawnCartCat = (btn) => {
    const cat = document.createElement('div');
    cat.className = 'qty-cat-pop';
    cat.innerHTML = `
      <div class="qcp__head">
        <div class="qcp__ear qcp__ear--l"></div>
        <div class="qcp__ear qcp__ear--r"></div>
        <div class="qcp__face">
          <div class="qcp__eyes"><span></span><span></span></div>
          <div class="qcp__nose"></div>
          <div class="qcp__mouth"></div>
        </div>
      </div>
      <div class="qcp__tail"></div>`;
    const rect = btn.getBoundingClientRect();
    cat.style.left = (rect.left + rect.width / 2 - 14) + 'px';
    cat.style.top  = (rect.top  - 6) + 'px';
    document.body.appendChild(cat);
    if (typeof gsap !== 'undefined') {
      gsap.fromTo(cat,
        { y: 0, opacity: 1, scale: 0.6 },
        { y: -44, opacity: 0, scale: 1, duration: 0.72, ease: 'power2.out',
          onComplete: () => cat.remove() }
      );
    } else {
      setTimeout(() => cat.remove(), 750);
    }
  };

  /* ══════════════════════════════════════════════
     8. CHECKOUT BUTTON — botanical leaf burst
  ══════════════════════════════════════════════ */
  document.getElementById('cart-checkout-btn')?.addEventListener('click', function (e) {
    e.preventDefault();
    const btn  = this;
    const href = btn.getAttribute('href');
    if (btn.classList.contains('is-blooming')) return;
    btn.classList.add('is-blooming');
    setTimeout(() => { window.location.href = href; }, 680);
  });

  /* ══════════════════════════════════════════════
     8. FAQ ACCORDION
  ══════════════════════════════════════════════ */
  document.querySelectorAll('.faq-item__q').forEach(btn => {
    btn.addEventListener('click', () => {
      const item   = btn.closest('.faq-item');
      const answer = item.querySelector('.faq-item__a');
      const isOpen = answer.classList.contains('is-open');

      /* close all others in same list */
      btn.closest('.faq-list').querySelectorAll('.faq-item__a.is-open').forEach(el => {
        el.classList.remove('is-open');
        el.previousElementSibling.setAttribute('aria-expanded', 'false');
      });

      if (!isOpen) {
        answer.classList.add('is-open');
        btn.setAttribute('aria-expanded', 'true');
      }
    });
  });

  fetchCart(); // init count on every page
});
